import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Card, Container, Divider, Grid} from "@mantine/core";
import {useCallback} from "react";
import {useApp} from "../../../../providers/RegionProvider/RegionProvider";
import {EntityTypeSelect} from "../../../select/entity/EntityTypeSelect/EntityTypeSelect";
import {
  CompanySiteAssignmentFormFieldValues,
  CompanySiteAssignmentFormProvider,
  CompanySiteAssignmentFormValues
} from "../CompanySiteAssignmentForm/form-context";
import {
  CompanySiteAssignmentFormFields,
  getCompanySiteAssignmentFormValues,
  getDefaultCompanySiteAssignmentFormValues
} from "../CompanySiteAssignmentForm/CompanySiteAssignmentForm";
import {EntityMultiSelect} from "../../../select/entity/EntityMultiSelect/EntityMultiSelect";
import {toTransportCategory, TransportCategory} from "@ots-frontend/ots-data";
import {TransportCategorySelect} from "../../../select/entity/TransportCategorySelect/TransportCategorySelect";

interface CompanySiteAssignmentBulkFormProps {
  options?: CompanySiteAssignmentBulkFormOptions;
  defaultValues?: Partial<CompanySiteAssignmentBulkFormValues>;
  onSubmit?: (values: CompanySiteAssignmentBulkFormValues) => void;
  submitting?: boolean;
}

export interface CompanySiteAssignmentBulkFormOptions {
  entitySelectionDisabled?: boolean
  categorySelectionDisabled?: boolean
}

export interface CompanySiteAssignmentBulkFormValues extends CompanySiteAssignmentFormValues {
  entityType: string
  entityIds: string[]
  category: TransportCategory
}

const getDefaultValues = (defaultValues: Partial<CompanySiteAssignmentBulkFormValues> | undefined) : CompanySiteAssignmentFormFieldValues => {
  return {
    ...getDefaultCompanySiteAssignmentFormValues(defaultValues),
    entityType: defaultValues?.entityType,
    entityIds: defaultValues?.entityIds,
    category: defaultValues?.category?.code,
  }
}

export const CompanySiteAssignmentBulkForm = (props: CompanySiteAssignmentBulkFormProps) => {
  const {
    options,
    defaultValues,
    onSubmit,
    submitting = false
  } = props;

  const {regionId} = useApp()

  const {t} = useTranslation(["common"]);

  const form = useForm({
    initialValues: getDefaultValues(defaultValues),
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {

      const formValues: CompanySiteAssignmentBulkFormValues = {
        ...getCompanySiteAssignmentFormValues(values),
        entityType: values.entityType!,
        entityIds: values.entityIds!,
        category: toTransportCategory(values.category!),
      }

      onSubmit(formValues);
    }
  }, [form, onSubmit]);

  return (
    <CompanySiteAssignmentFormProvider form={form}>
      <form onSubmit={form.onSubmit(onFormSubmit)}>
        <Grid>
          <Grid.Col span={8}>
            <Container fluid={true}>
              <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                <TransportCategorySelect
                  label={t("common.entity.siteassignment.form.field.category", "Category")}
                  placeholder={t<string>("common.entity.siteassignment.form.field.object.category", "Category")}
                  withinPortal={true}
                  disabled={options?.categorySelectionDisabled}
                  clearable
                  withAsterisk
                  required
                  {...form.getInputProps("category")} />
                <CompanySiteAssignmentFormFields options={{ regionId: regionId }} />
                <Divider my="sm" variant="dotted"/>
                <Button type="submit" loading={submitting} fullWidth mt="xl">
                  {t("common.action.submit", {ns: "common"})}
                </Button>
              </Card>
            </Container>
          </Grid.Col>
          <Grid.Col span={4}>
            <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
              <EntityTypeSelect
                options={['ROUTE', 'OPERATINGAREA']}
                label={t("common.entity.siteassignment.form.field.object", "Object")}
                placeholder={t<string>("common.entity.siteassignment.form.field.object.placeholder", "Object")}
                withinPortal={true}
                disabled={options?.entitySelectionDisabled}
                clearable
                withAsterisk
                required
                {...form.getInputProps("entityType")} />
              <EntityMultiSelect
                pt="md"
                regionId={regionId}
                entityType={form.getTransformedValues().entityType}
                withinPortal={true}
                disabled={options?.entitySelectionDisabled}
                clearable
                withAsterisk
                required
                {...form.getInputProps("entityIds")} />
            </Card>
          </Grid.Col>
        </Grid>
      </form>
    </CompanySiteAssignmentFormProvider>
  );
};
